import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { handleHttpError } from '../../helpers/handleHttpError';
import {
  DeviceDetailsResponseDto,
  DevicesResponseDto
} from '../../infrastructure/backends/devices.dto';
import { ApiRemoteUpdate } from '../../infrastructure/services/api-remote-update';
import { DevicesDataStoreType } from '../../infrastructure/types/device-api.types';

const initialState: DevicesDataStoreType = {
  devices: {
    data: null,
    loading: false,
    error: null
  },
  searchValue: '',
  deviceDetails: {
    data: null,
    loading: false,
    error: null
  },
  deviceUpdateRecordsFile: {
    loading: false,
    error: null,
    canceled: false
  }
};

export const useDeviceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(store => {
    const api = inject(ApiRemoteUpdate);

    return {
      clear() {
        patchState(store, {
          devices: {
            data: null,
            loading: false,
            error: null
          },
          searchValue: ''
        });
      },

      async loadNextPage(pageNumber: number, pageSize: number, filterValue?: string) {
        if (store.devices().loading) return;

        if (filterValue !== undefined) {
          patchState(store, { searchValue: filterValue });
        }

        const activeFilter = filterValue ?? store.searchValue();

        patchState(store, state => ({
          devices: { ...state.devices, loading: true }
        }));

        try {
          const data = await firstValueFrom(
            api.get<DevicesResponseDto>('/devices', {
              pageNumber: pageNumber.toString(),
              pageSize: pageSize.toString(),
              filter: activeFilter
            })
          );

          const shouldReplace = activeFilter !== '';

          patchState(store, state => ({
            devices: {
              ...state.devices,
              data: {
                ...data,
                items: shouldReplace ? data.items : [...data.items]
              },
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            devices: {
              ...state.devices,
              loading: false,
              error: handleHttpError(error, 'Failed to load devices')
            }
          }));
        }
      },

      async loadDeviceDetails(deviceId: number) {
        patchState(store, state => ({
          ...state,
          deviceDetails: { ...state.deviceDetails, loading: true }
        }));
        try {
          const data = await firstValueFrom(
            api.get<DeviceDetailsResponseDto>(`/device/${deviceId}`)
          );

          patchState(store, state => ({
            ...state,
            deviceDetails: { ...state.deviceDetails, data, loading: false, error: null }
          }));
        } catch (error) {
          patchState(store, state => ({
            ...state,
            deviceDetails: {
              ...state.deviceDetails,
              loading: false,
              error: handleHttpError(error, 'Failed to load device details')
            }
          }));
          throw error;
        }
      },

      async downloadDeviceUpdateRecordsFile() {
        patchState(store, state => ({
          deviceUpdateRecordsFile: {
            ...state.deviceUpdateRecordsFile,
            loading: true,
            canceled: state.deviceUpdateRecordsFile.canceled,
            error: null
          }
        }));
        try {
          const response = await firstValueFrom(api.getBlob('/deviceHistories/download'));
          const blob = new Blob([response.body ?? new Uint8Array()], {
            type: response.headers.get('Content-Type') ?? 'application/octet-stream'
          });

          const contentDisposition = response.headers.get('Content-Disposition') ?? '';
          const fileName = contentDisposition.split("filename*=UTF-8''")?.[1];

          if (!contentDisposition || !fileName)
            throw new Error('Failed to download device update records file');

          if (!store.deviceUpdateRecordsFile().canceled) {
            const url = globalThis.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            globalThis.URL.revokeObjectURL(url);
          }

          patchState(store, state => ({
            deviceUpdateRecordsFile: {
              ...state.deviceUpdateRecordsFile,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            deviceUpdateRecordsFile: {
              ...state.deviceUpdateRecordsFile,
              loading: false,
              error: handleHttpError(error, 'Failed to download device update records file')
            }
          }));
          throw error;
        }
      },

      cancelDownloadDeviceUpdateRecordsFile() {
        patchState(store, state => ({
          deviceUpdateRecordsFile: { ...state.deviceUpdateRecordsFile, canceled: true }
        }));
      }
    };
  })
);
